import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useFrontendConfig } from '../../context/FrontendConfigContext';
import { useI18n } from '../../context/useI18n';
import { getPanelView, getProgramsFromConfig, mergeActiveProgramsFromApi, getPublicBrandLogo } from '../../utils/frontendConfigMerge';
import { D28D_PANEL_CARD_DEFAULTS } from '../../utils/d28dPanelDefaults';
import { panelCardText } from '../../utils/panelI18n';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { resolveShellBrandLogo } from '../../utils/branding';

const PROGRAMS_DEFAULT = [
  {
    id: 'vital',
    name: 'Vital D28D',
    desc: 'Bienestar integral y salud femenina.',
    img: 'https://images.unsplash.com/photo-1518310383802-640c2de311b2?auto=format&fit=crop&w=800&q=80',
    accent: '#ffd700',
  },
  {
    id: 'pancitas',
    name: 'Pancitas Fit',
    desc: 'Entrenamiento especializado para embarazo.',
    img: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=800&q=80',
    accent: '#ffd700',
  },
  {
    id: 'virtual_d28d',
    name: 'Virtual D28D',
    desc: 'El programa clásico de transformación en 28 días.',
    img: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=800&q=80',
    accent: '#ffd700',
  },
];

const OPERATION_CARDS = [
  {
    id: 'liveclasses',
    view: 'adminliveclasses',
    when: (has) => has(['super_admin', 'admin_marca', 'admin_gimnasio', 'admin_gym', 'admin_d28d']),
  },
  {
    id: 'programs',
    view: 'programs',
    when: (has) => has(['super_admin', 'admin_d28d']),
  },
  {
    id: 'd28d-trainers',
    view: 'd28d-trainers',
    when: (has) => has(['super_admin', 'admin_d28d']),
  },
  {
    id: 'd28d-challenges-admin',
    view: 'd28d-challenges-admin',
    when: (has) => has(['super_admin', 'admin_d28d']),
  },
  {
    id: 'd28d-cycle-reports',
    view: 'd28d-cycle-reports',
    when: (has) => has(['super_admin', 'admin_d28d']),
  },
  {
    id: 'd28d-gallery',
    view: 'd28d-gallery',
    when: (has) => has(['super_admin', 'admin_d28d']),
  },
  {
    id: 'modulevigencias',
    view: 'modulevigencias',
    when: (has) => has(['super_admin', 'admin_d28d']),
  },
  {
    id: 'edith-d28d',
    view: 'edith-d28d',
    when: (has) => has(['super_admin', 'admin_d28d']),
  },
  {
    id: 'finance-d28d',
    view: 'finance-d28d',
    when: (has) => has(['super_admin', 'admin_d28d']),
  },
];

/** Gimnasios dentro de D28D: admin gym virtual opera desde Maestros o estas tarjetas. */
const GYM_ROLES = ['admin_gym_virtual', 'admin_marca', 'admin_gimnasio', 'admin_gym'];

/** Plataforma D28D: gestión de sedes marca blanca (sin duplicar maestros de operación). */
const PLATFORM_WHITE_LABEL_CARDS = [
  {
    id: 'organizations',
    view: 'organizations',
    when: (has) => has(['super_admin']),
  },
  {
    id: 'admingyms',
    view: 'admingyms',
    when: (has) => has(['super_admin']),
  },
  {
    id: 'adminusers',
    view: 'adminusers',
    when: (has) => has(['super_admin']),
  },
];

/**
 * Admin D28D — Maestros (catálogo / configuración).
 * Sin duplicar OPERACIÓN: clases, programas, hosts, premiaciones, reportes,
 * grabaciones y vigencias viven solo en OPERATION_CARDS.
 */
const PLATFORM_D28D_MASTER_CARDS = [
  {
    id: 'd28d-routines',
    view: 'd28d-routines',
    when: (has) => has(['admin_d28d']),
  },
  {
    id: 'gym-masters-plans',
    view: 'commercial-plans',
    when: (has) => has(['admin_d28d']),
  },
  {
    id: 'configurations',
    view: 'configurations',
    when: (has) => has(['admin_d28d']),
  },
];

const GYM_SEDE_CARDS = [
  {
    id: 'masters-hub',
    view: 'masters',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'admingyms',
    view: 'admingyms',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'gym-masters-cycles',
    view: 'gym-masters-cycles',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'gym-masters-programs',
    view: 'gym-masters-programs',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'gym-masters-plans',
    view: 'gym-masters-plans',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'gym-masters-routines',
    view: 'gym-masters-routines',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'adminusers',
    view: 'adminusers',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'd28d-trainers',
    view: 'd28d-trainers',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'liveclasses',
    view: 'adminliveclasses',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'd28d-gallery',
    view: 'd28d-gallery',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'gym-masters-challenges',
    view: 'gym-masters-challenges',
    when: (has) => has(GYM_ROLES),
  },
  {
    id: 'modulevigencias',
    view: 'modulevigencias',
    when: (has) => has(GYM_ROLES),
  },
];

function PanelCard({ t, config, panelId, cardId, cardCfg, img, onClick, lang }) {
  const fromCfg = panelCardText(t, config, panelId, cardId, 'title', lang);
  const fromDefault = panelId === 'd28d' ? D28D_PANEL_CARD_DEFAULTS[cardId]?.title : '';
  const title = fromCfg || fromDefault || cardId;
  const desc = panelCardText(t, config, panelId, cardId, 'desc', lang)
    || (panelId === 'd28d' ? D28D_PANEL_CARD_DEFAULTS[cardId]?.desc : '')
    || '';
  return (
    <div
      className={`card admin-panel-card ${img ? 'has-img' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      role="button"
      tabIndex={0}
    >
      {img ? <img src={img} alt={cardCfg?.alt || title} className="admin-panel-card-img" /> : null}
      <h3 className="d28d-section-title">{title}</h3>
      <p className="d28d-text-muted">{desc}</p>
      <button type="button" className="btn-card" onClick={(e) => { e.stopPropagation(); onClick(); }}>{t('panel.open', 'Abrir')}</button>
    </div>
  );
}

export default function D28DAdminView({ hasAnyRole, onNavigate, onPickProgram, onBack, isGymScoped = false }) {
  const { config } = useFrontendConfig();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [apiPrograms, setApiPrograms] = useState(null);
  const panel = useMemo(() => getPanelView(config, 'd28d', lang), [config, lang]);
  const heroLogoSrc = useMemo(() => {
    const fromBrand = resolveShellBrandLogo({
      publicLogo: getPublicBrandLogo(config),
      user,
      gymBrand: user?.gym || null,
    });
    const raw = fromBrand || '/favicon-d28d.png';
    return resolveMediaUrl(raw);
  }, [config, user]);
  const isGymOnlyOps = isGymScoped || (
    hasAnyRole(['admin_gym_virtual', 'admin_marca', 'admin_gimnasio', 'admin_gym'])
    && !hasAnyRole(['super_admin', 'admin_d28d'])
  );

  useEffect(() => {
    if (isGymOnlyOps) return;
    api.get('/programs', { params: { platform_only: 'true' } })
      .then((res) => setApiPrograms(res.data?.data || []))
      .catch(() => setApiPrograms([]));
  }, [isGymOnlyOps]);

  const programs = useMemo(() => {
    if (Array.isArray(apiPrograms)) {
      return mergeActiveProgramsFromApi(apiPrograms, config, PROGRAMS_DEFAULT, lang);
    }
    return getProgramsFromConfig(config, PROGRAMS_DEFAULT, lang);
  }, [apiPrograms, config, lang]);
  const isPlatformD28d = hasAnyRole(['super_admin', 'admin_d28d']);
  const isPlatformD28dAdminOnly = hasAnyRole(['admin_d28d']) && !hasAnyRole(['super_admin']);
  const whiteLabelCards = isPlatformD28d && !isGymOnlyOps
    ? (hasAnyRole(['super_admin']) ? PLATFORM_WHITE_LABEL_CARDS : PLATFORM_D28D_MASTER_CARDS)
    : GYM_SEDE_CARDS;
  const visibleWhiteLabelCards = whiteLabelCards.filter(
    (c) => !c.when || c.when(hasAnyRole),
  );

  return (
    <div className="dashboard-main-view">
      <header className="dashboard-header panel-admin-header">
        <div className="panel-admin-header-main">
          {heroLogoSrc ? (
            <img
              src={heroLogoSrc}
              alt=""
              className="panel-admin-hero-thumb panel-admin-hero-thumb--logo"
            />
          ) : null}
          <div>
            <button type="button" className="btn-secondary panel-back-btn" onClick={onBack}>
              {t('panel.back_services', '← Servicios')}
            </button>
            <h2 className="d28d-page-title">{panel.hero?.title || 'D28D · Programas'}</h2>
            <p className="d28d-text-muted">{panel.hero?.subtitle}</p>
          </div>
        </div>
      </header>

      {!isGymOnlyOps && (
        <>
          <h3 className="d28d-section-title" style={{ margin: '1rem 0 0.5rem' }}>{t('panel.active_programs', 'Programas activos')}</h3>
          <div className="services-hero-grid">
            {programs.map((p) => (
              <button
                key={p.id}
                type="button"
                className="service-card-hero"
                onClick={() => onPickProgram(p.id)}
                style={{
                  textAlign: 'left',
                  border: '1px solid var(--d28d-border)',
                  padding: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <img src={resolveMediaUrl(p.img)} alt={p.name} className="service-card-hero-img" />
                <div className="service-card-hero-content">
                  <h3 className="service-card-hero-title service-card-hero-title--accent">{p.name}</h3>
                  <p className="service-card-hero-desc">{p.desc}</p>
                </div>
              </button>
            ))}
          </div>

          <h3 className="d28d-section-title" style={{ margin: '1.5rem 0 0.5rem' }}>{t('panel.operation', 'Operación')}</h3>
          <div className="dashboard-grid">
            {OPERATION_CARDS.map((c) => {
              if (c.when && !c.when(hasAnyRole)) return null;
              const cardCfg = panel.cards?.[c.id] || {};
              const img = cardCfg.img ? resolveMediaUrl(cardCfg.img) : '';
              return (
                <PanelCard
                  key={c.id}
                  t={t}
                  config={config}
                  panelId="d28d"
                  cardId={c.id}
                  cardCfg={cardCfg}
                  img={img}
                  lang={lang}
                  onClick={() => onNavigate(c.view)}
                />
              );
            })}
          </div>
        </>
      )}

      {visibleWhiteLabelCards.length > 0 && (isPlatformD28d || isGymOnlyOps) && (
        <>
          <h3 className="d28d-section-title" style={{ margin: isGymOnlyOps ? '0 0 0.5rem' : '1.5rem 0 0.5rem' }}>
            {isGymOnlyOps
              ? t('nav.masters', 'Maestros')
              : isPlatformD28dAdminOnly
                ? t('panel.d28d_masters', 'Maestros D28D')
                : t('panel.white_label_gyms', 'Gimnasios marca blanca')}
          </h3>
          <p className="d28d-text-muted" style={{ marginTop: 0 }}>
            {isPlatformD28dAdminOnly
              ? t(
                'panel.d28d_masters_desc',
                'Catálogo y configuración del gimnasio virtual semilla: rutinas plantilla, planes comerciales y apariencia. Lo operativo del día a día está arriba en Operación.',
              )
              : isPlatformD28d && hasAnyRole(['super_admin'])
                ? t('panel.white_label_desc_admin', 'Crea sedes desde Gimnasios virtuales (Tenants). Cada sede opera en Maestros con datos independientes.')
                : t('panel.white_label_desc_user', 'Operación de tu sede: configura programas, ciclos, rutinas, clases, retos y planes comerciales.')}
          </p>
          {isGymOnlyOps ? (
            <button
              type="button"
              className="btn-primary text-sm mt-2 mb-3"
              onClick={() => onNavigate('masters')}
            >
              {t('gym.masters.open_hub', 'Abrir panel Maestros →')}
            </button>
          ) : null}
          <div className="dashboard-grid">
            {visibleWhiteLabelCards.map((c) => {
              const cardCfg = panel.cards?.[c.id] || {};
              const img = cardCfg.img ? resolveMediaUrl(cardCfg.img) : '';
              return (
                <PanelCard
                  key={c.id}
                  t={t}
                  config={config}
                  panelId="d28d"
                  cardId={c.id}
                  cardCfg={cardCfg}
                  img={img}
                  lang={lang}
                  onClick={() => onNavigate(c.view)}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

